/**
 * Decisions for resuming an interrupted media upload.
 *
 * THE BUG. Resuming reused the signed upload URL persisted on the device and never asked the
 * server for one. A signed URL is short-lived, so tus sent its offset probe (HEAD) to a dead URL,
 * got 400, and could not fall back to starting a fresh upload because the `tus.Upload` is
 * constructed with `uploadUrl` and no `endpoint`. tus reports that as:
 *
 *   "unable to resume upload (new upload cannot be created without an endpoint)"
 *
 * The upload was then unfinishable: the record stayed in "Resumable Uploads" and every retry
 * reproduced the identical failure.
 *
 * THE FIX IS TO CALL THE ENDPOINT THAT ALREADY EXISTED. `uploadMediaV2` on the agent-backend
 * (`src/services/supabase/SupabaseMediaService.ts`) has three outcomes, not two:
 *
 *   - a still-valid pending record (same file name + size + user) → returns THAT record's
 *     `upload_url`, so tus probes it, learns the offset and CONTINUES
 *   - a matched record past the window → DELETES that record and mints a NEW path with a NEW id
 *     → a clean upload from zero, which is correct because the earlier bytes are gone
 *   - NO record matched → mints a new path with a new id and deletes NOTHING. The bytes already on
 *     storage, and the row that names them, are simply abandoned.
 *
 * DO NOT REASON FROM A FIXED WINDOW. The replay branch is gated on `UPLOAD_VALIDITY_MINUTES` (120)
 * and on `status = 'pending'`, but `MediaUploadMonitor` independently drives rows to `failed` via
 * `checkUploadStatus`, so the effective window is shorter and not deterministic. Nothing here depends
 * on the number; what matters is that ALL THREE outcomes are routine, so the restart branch has to be
 * as correct as the continue branch rather than treated as an edge case.
 *
 * KNOWN, NOT FIXED HERE — a de-duplicated filename makes resume unreachable and leaks on every
 * attempt. The pending lookup (SupabaseMediaService.ts:333-341) matches `file_name = ${fileName}` —
 * the RAW name the client asked for — while the INSERT (:439) writes `uniqueFileName` from
 * `getUniqueFileName` (:489-514), which appends ` (1)` when a non-`failed` row already holds that
 * name in that folder. So a second `clip.mp4` is stored as `clip (1).mp4`, and a resume asking for
 * `clip.mp4` can never match it. Each Resume therefore takes the third branch above: restart from 0,
 * plus one more abandoned media row and one more partial storage object. Within the window the names
 * even escalate — `clip (2).mp4`, `clip (3).mp4` — because pending rows count as duplicates. The
 * abandoned rows are eventually flipped to `failed` by the monitor; the partial storage objects are
 * never collected.
 *
 * THIS IS NOT FIXABLE FROM THE CLIENT. The client does hold the server media id (`PersistedUpload.id`,
 * later `serverId`), but `POST /api/media/upload` accepts only file_name / file_size / agent_id /
 * user_id / user_path / analysis_instruction (`src/api/schemas/media.ts:21-34`) and its response
 * carries only `{ id, upload_url, upload_token }` — never the stored name. There is no field on which
 * a resume could address the row it started. The fix belongs in the backend: key the pending lookup
 * on an id supplied by the client, or match the stored name; and delete the unmatched pending row
 * before minting a replacement.
 *
 * A second lookup limit, unrelated to the above: it keys on `file_name` + `file_size` + a conditional
 * `user_id` — not `user_path`, not `agent_id` — so identically named and sized files in different
 * folders collide onto one record.
 *
 * WHAT THIS MODULE DELIBERATELY NO LONGER DOES. A previous version drove a cancel-and-retry
 * escalation recursively from inside tus's `onError`. That escalation called `cancelUpload`, which
 * DELETEs the upload resource, and fired on any failed probe — so a transient 503 destroyed every
 * uploaded byte of a large file. A refused probe is now surfaced, not recovered from, and the caller
 * re-requests on the next explicit Resume.
 */

/** The two facts that identify a dead upload URL, read off tus's DetailedError. */
export interface UploadFailureShape {
  /** `error.originalRequest.getMethod()` — null when the error carries no request. */
  method: string | null;
  /** `error.originalResponse.getStatus()` — null when the error carries no response. */
  status: number | null;
}

/**
 * Pull the request method and response status off an unknown error.
 *
 * Defensive because `onError` is typed `Error | DetailedError`: a plain Error (option validation,
 * a thrown callback) carries neither field, and reading them blindly would throw inside an error
 * handler.
 */
export const readUploadFailureShape = (error: unknown): UploadFailureShape => {
  const detailed = error as {
    originalRequest?: { getMethod?: () => string };
    originalResponse?: { getStatus?: () => number } | null;
  } | null;

  let method: string | null = null;
  let status: number | null = null;
  try {
    method = detailed?.originalRequest?.getMethod?.() ?? null;
    status = detailed?.originalResponse?.getStatus?.() ?? null;
  } catch {
    /* a malformed error object must not break the error path */
  }
  return { method, status };
};

/**
 * Is this failure "the URL we hold is gone", as opposed to a transport problem?
 *
 * STRUCTURAL, not textual, and that is the whole point. The previous version matched tus's error
 * MESSAGE, which was unsafe: tus emits "new upload cannot be created without an endpoint" for
 * EVERY non-2xx probe response when no `endpoint` is configured — including 5xx. So a brief
 * storage outage was read as a dead URL. Verified against tus-js-client 4.3.1.
 *
 * Only a refused HEAD counts:
 *  - HEAD is the offset probe. A 4xx there means the server will not tell us this upload's offset,
 *    and re-requesting is the only way forward.
 *  - a 4xx on a PATCH is a chunk-level problem, and re-requesting a URL would not fix it.
 *  - 5xx / network / timeout are transient: tus's own retries own those, and the URL is still fine.
 *
 * THE WHOLE 4xx RANGE, not just 400/404, because that is the range tus itself reacts to. Its
 * `_resumeUpload` tests `inStatusCategory(status, 400)` — 400-499 — and with no `endpoint`
 * configured emits the identical "unable to resume upload" DetailedError for every one of them
 * (tus-js-client 4.3.1, lib/upload.js:679-693). A signed URL that has expired or been invalidated can
 * answer 410 as easily as 400 or 404, and narrowing to two codes left those uploads stuck in exactly
 * the loop this module exists to break: no record discarded, the same dead URL replayed by the next
 * Resume, forever. The range, minus the codes below, is the safe way to cover them.
 *
 * FIVE 4xx CODES ARE EXCLUDED, and the reason is the same for all five: the recovery gated on this
 * predicate is a `cancelUpload`, which DELETEs the upload resource and every byte already stored.
 * A code that has ANY reading other than "this upload is gone" must not reach it.
 *
 *  - 408 (Request Timeout) and 429 (Too Many Requests) mean "ask again", not "this upload is gone".
 *    Treating a rate-limit as a dead URL would destroy a large upload that was merely throttled.
 *
 *  - 409 (Conflict) and 423 (Locked) are TRANSIENT BY TUS'S OWN DEFINITION, and this is the one place
 *    that must not disagree with it. `defaultOnShouldRetry` retries exactly these two out of the whole
 *    4xx range (lib/upload.js:1080), and `_resumeUpload` special-cases 423 because "an upload is
 *    usually locked for a short period of time and will be available afterwards" (:669-679). A lock is
 *    routine — a concurrent probe, or a server-side lock left by an aborted PATCH. Reading it as a dead
 *    URL would delete a multi-GB upload that was unavailable for two seconds. Note this file's
 *    `onShouldRetry` overrides tus's classification and retries any error, so both codes DO reach
 *    `onError` and would otherwise land here.
 *
 *  - 401 and 403 are AMBIGUOUS: they can mean "this URL/token is gone", but they can equally mean
 *    "our session is". The probe carries `Authorization: Bearer <app JWT>`, and tus owns its own XHR,
 *    so it never passes through `Http.ts`'s response interceptor — the only thing in the app that
 *    refreshes on a 401 and retries. A signed URL that is perfectly valid therefore answers 401 here
 *    whenever the session behind it is not.
 *
 *    The common cause of that — a token frozen at `tus.Upload` construction going stale during a long
 *    transfer — is fixed at its root by the `onBeforeRequest` hook in `media-upload-context`, which
 *    re-reads the header per request. What remains is a genuinely expired session, and for THAT the
 *    only safe answer is to do nothing destructive.
 *
 *    THE ASYMMETRY IS THE WHOLE ARGUMENT. Wrongly treating 401/403 as dead runs `cancelUpload`, which
 *    DELETEs the record and every stored byte of a healthy upload — unrecoverable, and strictly worse
 *    than the bug this module fixes, which at least destroyed nothing. Wrongly treating a genuinely
 *    dead URL as live costs a failed Resume that the user can press again; the record stays in
 *    "Resumable Uploads", and once the session recovers the next press re-requests and proceeds. Note
 *    the recovery is NOT automatic on this path: `MediaService.getUploadUrl` posts through the bare
 *    `axios` default export rather than the `Http` instance, so it carries no refresh interceptor
 *    either and will 401 alongside everything else until the session is renewed elsewhere. A stuck
 *    Resume is still the cheaper of the two failures.
 */
export const isDeadUploadUrl = ({ method, status }: UploadFailureShape): boolean =>
  method === 'HEAD' &&
  status !== null &&
  status >= 400 &&
  status < 500 &&
  status !== 401 &&
  status !== 403 &&
  status !== 408 &&
  status !== 409 &&
  status !== 423 &&
  status !== 429;

/**
 * A human message for a refused offset probe that is NOT a dead URL.
 *
 * The companion to the exclusions above, and the reason they do not cost the user anything legible.
 * Every code `isDeadUploadUrl` excludes now falls through to the generic failure path, which reports
 * tus's own text — "tus: unable to resume upload (new upload cannot be created without an endpoint)"
 * — the opaque string this whole module exists to stop showing people. The exclusions are about not
 * DELETING the record; they were never about saying less.
 *
 * Returns null for anything that is not a recognised probe refusal, so the caller keeps reporting the
 * real error rather than papering over an unknown failure with a friendly guess.
 */
export const describeRefusedProbe = ({ method, status }: UploadFailureShape): string | null => {
  if (method !== 'HEAD' || status === null) return null;
  // CONTROL-NEUTRAL wording — "try again", never "press Resume". The same message reaches two
  // surfaces with different buttons: the media library offers Resume, while a request attachment
  // that fails is rendered by `uploading-status-cell`, which offers Retry and has no Resume anywhere.
  // Naming the wrong control is worse than naming none.
  if (status === 401 || status === 403) {
    return 'Your session expired while this upload was running. Sign in again, then try it again.';
  }
  if (status === 409 || status === 423) {
    return 'This upload is briefly locked on the server. Try it again in a moment.';
  }
  if (status === 408 || status === 429) {
    return 'The server is busy. Try this upload again in a moment.';
  }
  return null;
};

/**
 * Progress to show for a resumed upload, given the id the server just handed back.
 *
 * The server returns the SAME id when it is continuing an existing record, and a NEW id when it
 * has discarded that record and minted a fresh path. In the second case the previous progress is
 * meaningless — nothing on the server holds those bytes any more — so showing the old percentage
 * would tick backwards on the first real update and push a regression into the progress store.
 */
export const resumeStartProgress = (
  previousId: string | null | undefined,
  serverId: string | null | undefined,
  previousProgress: number,
): number => (!!serverId && serverId === previousId ? previousProgress : 0);

/** Minimal shape of a persisted-upload row — structural so this module stays dependency-free. */
export interface PersistedRowLike {
  id: string;
}

/**
 * Fold the server's answer into the existing persisted row, in place.
 *
 * Three properties, each one a bug that already happened — all pinned by tests:
 *
 *  1. It UPDATES, never deletes-and-reinserts, so the row keeps its id and stays matchable by readers
 *     outside this provider that key on `PendingUpload.tempId` and are never remapped.
 *  2. It never CREATES, so a row the user dismissed mid-resume stays dismissed. An append-based
 *     version resurrected it back into Resumable Uploads and into localStorage.
 *  3. The row is never ABSENT for a moment. That absence was the reported bug: the restart branch
 *     deleted the only row and created nothing, so a later failure had no record to resume from.
 *
 * A patch rather than a whole row, so the field list cannot drift from the one `doUpload` builds.
 */
export const applyResumePersistence = <T extends PersistedRowLike>(
  prev: readonly T[],
  id: string,
  patch: Partial<Omit<T, 'id'>>,
): T[] => prev.map((row) => (row.id === id ? { ...row, ...patch } : row));

/**
 * Merge one provider instance's rows into what is already in localStorage.
 *
 * THE BUG THIS REPLACES. `MediaUploadProvider` mirrors the persisted list in React state and wrote
 * that whole array to localStorage on every change. More than one instance is mounted at a time —
 * the Revisions page nests one inside another, and the media-library page coexists with the request
 * wizard's — and each instance reads storage exactly once, at mount. So its mirror can never contain
 * a row a SIBLING created afterwards, and a blind write published that ignorance by deleting the
 * sibling's rows.
 *
 * The completion broadcast made it reliable rather than merely possible: it fires on every successful
 * upload and its handler calls `setPersistedUploads` in EVERY mounted instance, so every stale mirror
 * was flushed to storage on every completion. An upload still in flight lost its persisted row, and
 * if it then failed there was no record to resume from and its IndexedDB blob was orphaned.
 *
 * THE THREE-WAY DISTINCTION that makes a merge possible at all. An id in storage but not in `mine` is
 * ambiguous on its own, so `known` — every id this instance has ever held — separates the cases:
 *
 *  - in `mine`                        → this instance has an opinion; see the tie-break below
 *  - not in `mine`, not in `known`    → a sibling's row this instance has never seen → PRESERVE
 *  - not in `mine`, but in `known`    → this instance held it and dropped it → a REMOVAL
 *
 * THE TIE-BREAK when both hold a row. Ours wins, except when we can prove we have not touched our
 * copy since our last write AND the stored copy has changed since then — that change is a sibling's
 * and ours is merely old. `lastWrittenOwn` (id → serialised own row at last write) is what makes that
 * provable. Without it an idle instance flushed its mount-time `serverId` and `uploadUrl` over the
 * values a sibling had just learned by resuming, and `use-retry-failed-upload` reads its row straight
 * from localStorage, so the next retry resumed against the stale ones.
 *
 * Pure, and returns the next array rather than writing it, so all of the above is testable without a
 * React tree or a DOM.
 */
export const mergePersistedUploadsForWrite = <T extends PersistedRowLike>(params: {
  /** What is in localStorage right now. */
  stored: readonly T[];
  /** This instance's rows. */
  mine: readonly T[];
  /** Every id this instance has ever held, including the ones in `mine`. */
  known: ReadonlySet<string>;
  /** id → `JSON.stringify` of this instance's own copy as of its last write. */
  lastWrittenOwn: ReadonlyMap<string, string>;
}): T[] => {
  const { stored, mine, known, lastWrittenOwn } = params;
  const mineById = new Map(mine.map((row) => [row.id, row]));

  const pickRow = (storedRow: T): T => {
    const ours = mineById.get(storedRow.id);
    if (!ours) return storedRow;
    const lastOurs = lastWrittenOwn.get(storedRow.id);
    // No record of a previous write means this row is newly ours — created or just patched here.
    if (lastOurs === undefined) return ours;
    if (JSON.stringify(ours) !== lastOurs) return ours; // we changed it since that write
    // Our copy is untouched. If the stored copy moved, the mover was someone else.
    return JSON.stringify(storedRow) === lastOurs ? ours : storedRow;
  };

  const merged = stored.filter((row) => mineById.has(row.id) || !known.has(row.id)).map(pickRow);
  const storedIds = new Set(stored.map((row) => row.id));
  return [...merged, ...mine.filter((row) => !storedIds.has(row.id))];
};

/**
 * Snapshot rows as `lastWrittenOwn` for the next `mergePersistedUploadsForWrite`.
 *
 * Wanted in TWO places, and having it in only one of them was a bug. The write effect builds this
 * after every write, which is obvious. The MOUNT-TIME LOAD needs it too, which was not: a load is an
 * instance's first copy of every row it reads, so it is precisely the "I am merely holding an old
 * copy" case the map exists to identify — but with no entry for the row, `pickRow` takes the
 * `lastOurs === undefined` branch and reads it as one this instance just created, keeping it
 * unconditionally.
 *
 * What that cost: provider A resumes upload X and writes a fresh serverId / uploadUrl / progress;
 * provider B, which only ever loaded X at mount, runs its write effect for an unrelated reason — a
 * progress tick on any other upload is enough — and flushes its stale mount-time copy over A's. The
 * next Resume then replays the dead upload URL, which is the whole failure this module removes.
 *
 * Rows with no id are skipped rather than keyed on `undefined`: the persisted list is rehydrated from
 * localStorage and is only as well-formed as whatever last wrote it.
 */
export const serialiseOwnRows = <T extends PersistedRowLike>(
  rows: readonly (T | null | undefined)[],
): Map<string, string> => {
  const snapshot = new Map<string, string>();
  rows.forEach((row) => {
    if (row?.id) snapshot.set(row.id, JSON.stringify(row));
  });
  return snapshot;
};

/**
 * Which brand a resume should be scoped to.
 *
 * The backend's pending-record lookup keys on the brand (user_id), so a resume must re-request
 * under the brand the upload STARTED under — not whichever is active when the user comes back.
 * On a multi-brand account, switching brands before resuming otherwise attributes the media to the
 * wrong brand or has it rejected.
 *
 * Falls back to the active brand only when the persisted record predates brandId being captured;
 * those entries would otherwise become unresumable.
 */
export const resumeBrandScope = (
  persistedBrandId: string | null | undefined,
  activeBrandId: string | null | undefined,
): string | undefined => persistedBrandId || activeBrandId || undefined;

/**
 * How long a resume will wait for an in-flight discard before going ahead anyway.
 *
 * Shorter than the HTTP client's own 20s ceiling (`Http.ts`) on purpose. Waiting is an optimisation
 * over the old behaviour, never a new dependency: if the cancel hangs, pressing Resume must still do
 * something. Timing out lands exactly on the pre-fix behaviour — a possible replay of the dead URL —
 * whereas waiting out a 20s request would turn the button itself into the dead end this PR removes.
 */
export const DEAD_RECORD_DISCARD_WAIT_MS = 5000;

/**
 * Serialises "discard the dead record" against "resume this record".
 *
 * THE RACE THIS CLOSES. When a resume's offset probe is refused, the client discards the pending
 * record (`cancelUpload`) and tells the user to press Resume again. The discard is async and the
 * message is immediate, so the second press could re-request a URL while the record was still
 * pending — and the backend answers a still-pending match with THAT record's `upload_url`, i.e. the
 * same dead URL, reproducing the loop this feature exists to break. Nothing else serialised them:
 * `acquireResumeLock` was already released (its attempt returned when `upload.start()` was called),
 * and the tus registry entry was already dropped by the failure handler.
 *
 * WHY A GATE AND NOT AN `await` IN THE ERROR HANDLER. Awaiting the discard before showing the
 * message only narrows the window — the row is already 'failed' and clickable while the await runs.
 * The wait has to happen on the RESUME side, which is also the only place that can bound it.
 *
 * Deliberately not another loose module-level Set: one named primitive, tested here, rather than a
 * fourth ad-hoc map whose contract lives in comments at its use sites.
 *
 * Not a lock and never a rejection: `wait` resolves when the discard settles, times out, or was
 * never armed, and a discard that throws still releases. A gate that can refuse or hang would be a
 * new dead end, which is the one outcome this whole PR is about not shipping.
 */
export interface DeadRecordDiscardGate {
  /**
   * Track a discard for `id`. Returns a promise that never rejects; callers may ignore it.
   * An absent `id` (a record with no client id) simply runs ungated — there is nothing to key on.
   */
  run: (id: string | undefined, discard: () => Promise<unknown>) => Promise<void>;
  /** Resolve once no discard for `id` is outstanding, or the wait budget is spent. */
  wait: (id: string) => Promise<void>;
}

export const createDeadRecordDiscardGate = (
  timeoutMs: number = DEAD_RECORD_DISCARD_WAIT_MS,
): DeadRecordDiscardGate => {
  const inFlight = new Map<string, Promise<void>>();

  const run = (id: string | undefined, discard: () => Promise<unknown>): Promise<void> => {
    // Both handlers collapse to undefined: a failed discard is still a finished discard, and the
    // discard itself owns the logging. Rejecting here would surface as an unhandled rejection at the
    // fire-and-forget call site.
    const settled = Promise.resolve()
      .then(discard)
      .then(
        () => undefined,
        () => undefined,
      );
    if (!id) return settled;

    // Chained onto any previous attempt for the same id, so `wait` covers every outstanding discard
    // rather than only the newest. Chaining affects when the WAIT resolves, not when the requests
    // run — both are already in flight.
    const previous = inFlight.get(id);
    const attempt = previous ? previous.then(() => settled) : settled;
    inFlight.set(id, attempt);
    // Identity-checked, so a later attempt that has replaced this one is not evicted by this one
    // finishing.
    void attempt.then(() => {
      if (inFlight.get(id) === attempt) inFlight.delete(id);
    });
    return attempt;
  };

  const wait = (id: string): Promise<void> => {
    const attempt = inFlight.get(id);
    if (!attempt) return Promise.resolve();
    if (timeoutMs <= 0) return attempt;
    return new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, timeoutMs);
      void attempt.then(() => {
        clearTimeout(timer);
        resolve();
      });
    });
  };

  return { run, wait };
};
